import { Router } from '@angular/router';
import { Injectable, inject, signal, computed } from '@angular/core';
import { AuthService } from '../shared/services/auth.service';
import { StorageService } from '../shared/db/StorageService';
import { ConfigService } from '../pages/config/services/config.service';
import { AppConfig } from '../pages/config/app-config.interface';
import type { UserRole } from '../shared/models/session.model';
import { RoleService } from '../shared/services/role.service';

/**
 * The Context defines the interface of interest to clients. It also maintains a
 * reference to an instance of a State subclass, which represents the current
 * state of the Context.
 */

/** Names of all menu states — used by guard and AppComponent. */
export const MENU_STATE_NAMES: ReadonlySet<string> = new Set([
  'MenuState',           // legacy resolver
  'AdminMenuState',
  'MarketerMenuState',
]);

/** Resolve the concrete menu state class for a given role. */
function menuStateForRole(role: UserRole): AdminMenuState | MarketerMenuState {
  return role === 'admin'
    ? new AdminMenuState()
    : new MarketerMenuState();
}

@Injectable({
  providedIn: 'root',
})
export class GeneralStateContext {
  router = inject(Router);
  authService = inject(AuthService);
  configService = inject(ConfigService);
  roleService = inject(RoleService);
  private storageService = inject(StorageService);

  /**
   * @type {State} A reference to the current state of the Context.
   */
  private state: State;
  private storeKey = 'app-general-state';

  private stateSignal = signal<string>('ConfigState');
  isMenuState = computed(() => MENU_STATE_NAMES.has(this.stateSignal()));

  public async initializeState() {
    // Ya inicializado: el estado en memoria es la fuente de verdad.
    // Sin esto, cada navegación releía y re-escribía storage.
    if (this.state) {
      return;
    }
    const stateName = await this.getStateName();
    if (!stateName) {
      // DEFAULT State
      await this.transitionTo(new ConfigState());
    } else {
      await this.transitionTo(this.mapContextString[stateName]);
    }
  }

  /**
   * The Context allows changing the State object at runtime.
   */
  public async transitionTo(state: State): Promise<void> {
    // Solo persistir cuando el estado realmente cambia — antes se
    // escribía en storage en cada navegación aunque no cambiara.
    const changed = this.state?.constructor.name !== state.constructor.name;
    this.state = state;
    this.state.setContext(this);
    this.stateSignal.set(state.constructor.name);
    if (changed) {
      await this.saveState();
    }
  }

  public async getStateName() {
    if (this.state) {
      return this.state.constructor.name;
    }
    return await this.storageService.findByKey(this.storeKey);
  }

  /**
   * Resetea el estado en memoria: tras clearAll (deleteAccount) la próxima
   * initializeState() re-lee el storage vacío y arranca en ConfigState.
   */
  public resetState() {
    this.state = undefined as unknown as State;
  }

  private async saveState() {
    await this.storageService.store(this.storeKey, this.state.constructor.name);
  }

  public validate(): Promise<void> {
    return this.state.validate();
  }

  public navigateTo(): void {
    this.state.navigateTo();
  }

  public mapContextString: Record<string, State> = {
    LoginState: new LoginState(),
    MenuState: new MenuState(),               // legacy resolver
    AdminMenuState: new AdminMenuState(),
    MarketerMenuState: new MarketerMenuState(),
    ConfigState: new ConfigState(),
  };
}

/**
 * The base State class declares methods that all Concrete State should
 * implement and also provides a backreference to the Context object, associated
 * with the State. This backreference can be used by States to transition the
 * Context to another State.
 */
abstract class State {
  protected context: GeneralStateContext;

  public setContext(context: GeneralStateContext) {
    this.context = context;
  }

  public abstract validate(): Promise<void>;
  public abstract navigateTo(): void;
}

/**
 * Concrete States implement various behaviors, associated with a state of the
 * Context.
 */
export class LoginState extends State {
  public async validate(): Promise<void> {
    const session = await this.context.authService.getSessionInfo();
    if (session) {
      await this.context.transitionTo(menuStateForRole(session.role));
    }
  }

  public navigateTo(): void {
    this.context.router.navigate(['login']);
  }
}

/**
 * Legacy MenuState — only resolves migration.
 * Persisted sessions from before roles still have 'MenuState' in storage.
 * This state transitions immediately to the correct role-based state,
 * providing transparent migration without wiping storage.
 */
export class MenuState extends State {
  public async validate(): Promise<void> {
    const session = await this.context.authService.getSessionInfo();
    if (!session) {
      await this.context.transitionTo(new LoginState());
      return;
    }
    await this.context.transitionTo(menuStateForRole(session.role));
  }

  public navigateTo(): void {
    this.context.router.navigate(['dashboard']);
  }
}

/**
 * Menu state for admin users (full access, desktop/online-first).
 */
export class AdminMenuState extends State {
  public async validate(): Promise<void> {
    const session = await this.context.authService.getSessionInfo();
    if (!session) {
      await this.context.transitionTo(new LoginState());
      return;
    }
    // Auto-corrección de deriva de rol: si la sesión cambió (mismo dispositivo,
    // otro usuario), transiciona al estado correcto.
    if (session.role !== 'admin') {
      await this.context.transitionTo(new MarketerMenuState());
    }
  }

  public navigateTo(): void {
    this.context.router.navigate(['dashboard']);
  }
}

/**
 * Menu state for marketer users (limited access, mobile/offline-first).
 */
export class MarketerMenuState extends State {
  public async validate(): Promise<void> {
    const session = await this.context.authService.getSessionInfo();
    if (!session) {
      await this.context.transitionTo(new LoginState());
      return;
    }
    // Auto-corrección simétrica.
    if (session.role !== 'marketer') {
      await this.context.transitionTo(new AdminMenuState());
    }
  }

  public navigateTo(): void {
    this.context.router.navigate(['dashboard']);
  }
}

export class ConfigState extends State {
  public async validate(): Promise<void> {
    const clientDefinition: AppConfig | null =
      await this.context.configService.getAppConfig();
    if (clientDefinition) {
      await this.context.transitionTo(new LoginState());
    }
  }

  public navigateTo(): void {
    this.context.router.navigate(['config']);
  }
}
